/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { Grid, Stack, Typography, Card, Button } from "@mui/material";
import { useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { useParams } from "react-router";

// Component imports
import ScanningStatsCard from "./components/ScanningStatsCard";
import ExpiryStatsCard from "./components/ExpiryStatsCard";
import RevenueImpactCard from "./components/RevenueImpactCard";
import LineChartToggle from "./lineChartToggle";
// import RadarChart from "./RadarChart";
import NearingExpiryCard from "./components/NearingExpiryCard";

// Data imports
import {
  scanningStats,
  expiryStats,
  productCategories,
} from "../../../data/insightData";
import { DownloadIcon } from "lucide-react";
import UserThroughputCard from "./components/ThroughputCard";
import { fetchAndDownloadCSV } from "api/overView";
import axios from "axios";

function Overview({ dates }) {
  // const { storeId } = useParams();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [scanStats, setScanStats] = useState(scanningStats);
  const [expStats, setExpStats] = useState(expiryStats);
  const [prodCategories, setProdCategories] = useState(productCategories);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);

  const selectedDate = useSelector((state) => state.customization.selectedDate);

  const { storeId } = useParams(); // Extracts "S004" from URL

  useEffect(() => {
    // Add your data fetching logic here
    return () => {
      // Cleanup logic if needed
    };
  }, [storeId, selectedDate]);

  useEffect(() => {
    const storedUserData = localStorage.getItem("userData");
    if (storedUserData) {
      setUserData(JSON.parse(storedUserData));
    }
  }, []);

  const fetchDownloadCSV = async () => {
    const abortController = new AbortController();
    setLoading(true);

    try {
      const response = await fetchAndDownloadCSV(
        userData.id,
        selectedDate || new Date(),
        abortController.signal
      );

      if (!response.data.data || !response.data.data.length) {
        throw new Error("No data available");
      }

      const csvData = convertToCSV(response.data.data);
      downloadCSV(csvData, `inventory_data_${new Date().toISOString().split("T")[0].replace(/[-]/g, "_")}_${new Date().toTimeString().split(" ")[0].replace(/:/g, "_")}.csv`);
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Request cancelled");
      } else {
        console.error("Error fetching throughput data:", error);
      }
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
        abortController.abort();
      }
    }
  };

  const convertToCSV = (data) => {
    const headers = Object.keys(data[0]).join(",");

    const rows = data.map(obj =>
      Object.values(obj).map(value =>
        value !== null ? `"${value}"` : '""'
      ).join(",")
    );

    return [headers, ...rows].join("\n");
  };

  const downloadCSV = (csvContent, fileName) => {
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="w-full p-2">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h3">Overview</Typography>

            {isSmallScreen ? (
              <Button
                onClick={fetchDownloadCSV}
                disabled={loading}
                sx={{
                  minWidth: "auto",
                  padding: 0,
                  backgroundColor: "transparent",
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                <DownloadIcon size={20} />
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={fetchDownloadCSV}
                disabled={loading}
              >
                {loading ? "Downloading..." : "Download"}
              </Button>
            )}
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={1.5}>
            <ScanningStatsCard />
            <ExpiryStatsCard />
            <NearingExpiryCard />
            <RevenueImpactCard />
            <UserThroughputCard />
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <LineChartToggle dates={[selectedDate?.toISOString()]} />
        </Grid>
      </Grid>
    </div>
  );
}

Overview.propTypes = {
  dates: PropTypes.object,
};

export default Overview;
